// drawSummaryPanel.js
import { Style, Fill, Stroke } from 'ol/style';
import { createStyleFromColor, resetColorCycle, syncColorCycle } from './colorUtils.js';
import * as MapUtils from './mapUtils.js';
import { getDrawSource, removeFromSelectionCount } from './addressSearch.js';
import Collection from 'ol/Collection';
import Modify from 'ol/interaction/Modify.js';
import { getMap } from './mapInstance.js';
import { Circle as CircleStyle } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import { fromCircle } from 'ol/geom/Polygon';
import CircleGeom from 'ol/geom/Circle';
import { transform } from 'ol/proj';
import { createEmpty, extend, isEmpty } from 'ol/extent';
import { secureFetch } from './securityUtil.js';
import * as config from './config.js';
import { getSelectedCountryCode, getSelectedLanguageCode } from './country-language.js';
import { addToSelectionCount, resetSelectionCount,  clearSelectionSet} from './addressSearch.js';
import { hasRight } from './rightsUtil.js';
import { registerDock, setDockVisible } from './panelDockManager.js';
import { placeFloatingDialog } from './dialogs.js';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { unByKey } from 'ol/Observable.js';
import { geometryToShape, sendPostMessage } from './postMessages.js';
import { applyLanguageTranslations, translate } from './languageTranslator.js';
import { hideLoadingBar, showLoadingBar } from './loadingIndicator.js';

let drawHistory = [];
let addressSummaryPage = 1;
const ADDRESS_SUMMARY_PAGE_SIZE = 5;
let panel = null;
let selectedFeature = null;
let modifyInteraction = null;
let originalGeometry = null;
let vertexLayer = null;
let vertexSource = null;
let vertexGeometryChangeKey = null;
let editEscHandler = null;
let panelPosition = null;
let summaryMinimized = false;
let summaryStoredGeometry = null;
let summaryDockButton = null;
let summaryOpacity = 1;
let pendingSavePromise = Promise.resolve();
let saveInFlight = false;
let saveDirty = false;

function getAddressSelectionMessageData(item) {
  const payload = {
    totalHouseholds: Number(item?.count) || 0,
    ID: item?.seq,
    color: item?.color || '#000000',
    shape: geometryToShape(item?.feature?.getGeometry?.())
  };

  const addresses = item?.feature?.get?.('addressData');
  if (Array.isArray(addresses)) {
    const populatedRows = addresses.filter(row =>
      row && typeof row === 'object' &&
      Object.values(row).some(value =>
        value !== null && value !== undefined &&
        (typeof value !== 'string' || value.trim() !== '')
      )
    );

    if (populatedRows.length) {
      payload.addresses = populatedRows;
    }
  }

  return payload;
}

function sendAddressSelectionMessage(action, item) {
  if (!item) return;
  sendPostMessage(`addressselection:${action}`, getAddressSelectionMessageData(item));
}

function sendAddressSelectionSnapshot(items = drawHistory) {
  sendPostMessage('addressselection:snapshot', {
    selections: items.map(getAddressSelectionMessageData)
  });
}

export function getDrawSummaryColors() {
  return drawHistory.map(item => item?.color).filter(Boolean);
}

function applyLabelStyle(item) {
  if (!item?.feature || !item.color) return;

  item.feature.set('selectionNumber', item.seq);
  item.feature.setStyle(createStyleFromColor(item.color, item.seq));
}

function refreshLabelStyles() {
  drawHistory.forEach(applyLabelStyle);
}

function queueSaveToBackend() {
  saveDirty = true;
  if (!saveInFlight) {
    pendingSavePromise = drainSaveQueue();
  }
  return pendingSavePromise;
}

async function drainSaveQueue() {
  saveInFlight = true;

  try {
    while (saveDirty) {
      saveDirty = false;
      try {
        await saveToBackend();
      } catch (err) {
        console.error('Save queue failed:', err);
        MapUtils.setStatus('Address selection save failed. Please try again.');
      }
    }
  } finally {
    saveInFlight = false;
  }

  if (saveDirty) {
    return queueSaveToBackend();
  }
}

function propertiesToAddressRow(properties) {
  const row = {};

  if (Array.isArray(properties)) {
    properties.forEach(property => {
      const key = property?.key;
      if (key) row[key] = property.value;
    });
    return row;
  }

  if (properties && typeof properties === 'object') {
    Object.entries(properties).forEach(([key, value]) => {
      if (key) row[key] = value;
    });
  }

  return row;
}

function getSpatialResultCount(data) {
  const total = Number(data?.total);
  if (Number.isFinite(total)) return total;

  const totalFeatures = Number(data?.totalFeatures ?? data?.numberMatched ?? data?.numberReturned);
  if (Number.isFinite(totalFeatures)) return totalFeatures;

  return Array.isArray(data?.features) ? data.features.length : 0;
}

function getSpatialResultFeatures(data) {
  return Array.isArray(data?.features) ? data.features : [];
}

function rememberPanelPosition() {
  if (!panel) return;

  panelPosition = {
    left: panel.style.left,
    top: panel.style.top,
    right: panel.style.right,
    bottom: panel.style.bottom
  };
}

function applyPanelPosition() {
  if (!panel || !panelPosition) return;

  panel.style.left = panelPosition.left || '';
  panel.style.top = panelPosition.top || '10px';
  panel.style.right = panelPosition.right || '';
  panel.style.bottom = panelPosition.bottom || '';
}

function capturePanelGeometry() {
  if (!panel) return null;

  return {
    left: panel.style.left || '',
    top: panel.style.top || '',
    right: panel.style.right || '',
    bottom: panel.style.bottom || '',
    width: panel.style.width || '',
    height: panel.style.height || ''
  };
}

function applyStoredGeometry(geometry) {
  if (!panel || !geometry) return;

  if (geometry.left) panel.style.setProperty('left', geometry.left, 'important');
  else panel.style.removeProperty('left');
  if (geometry.top) panel.style.setProperty('top', geometry.top, 'important');
  else panel.style.removeProperty('top');
  if (geometry.right) panel.style.setProperty('right', geometry.right, 'important');
  else panel.style.removeProperty('right');
  if (geometry.bottom) panel.style.setProperty('bottom', geometry.bottom, 'important');
  else panel.style.removeProperty('bottom');
  if (geometry.width) panel.style.setProperty('width', geometry.width, 'important');
  else panel.style.removeProperty('width');
  panel.style.removeProperty('height');
}

function showSummaryPanel() {
  if (!panel) return;
  panel.style.display = 'block';
  panel.classList.remove('is-minimized');
  if (summaryStoredGeometry) {
    applyStoredGeometry(summaryStoredGeometry);
  }
  setDockVisible('draw-summary', false);
  summaryMinimized = false;
  requestAnimationFrame(() => placeFloatingDialog(panel));
}

function minimizeSummaryPanel() {
  if (!panel) return;
  summaryStoredGeometry = capturePanelGeometry();
  summaryMinimized = true;
  panel.classList.add('is-minimized');
  panel.style.display = 'none';
  setDockVisible('draw-summary', true);
}

function closeSummaryPanel() {
  setDockVisible('draw-summary', false);
  summaryMinimized = false;
  summaryStoredGeometry = null;
  if (summaryDockButton) {
    summaryDockButton.remove();
    summaryDockButton = null;
  }
  if (panel) {
    panel.remove();
    panel = null;
  }
}

document.addEventListener('address-selection-layer-hidden', () => closeSummaryPanel());

function setEditVertexStatus() {
  MapUtils.setCommand('Edit Vertex');
  MapUtils.setStatus('Drag polygon vertices. Press ESC to cancel current edit.');
  MapUtils.setMessage('Only the selected polygon vertices are editable.');
}

function updateEditButtonState() {
  if (!panel) return;

  panel.querySelectorAll(".edit-row-btn").forEach(btn => {
    const item = drawHistory[btn.getAttribute("data-index")];
    const isActive = !!modifyInteraction && item?.feature === selectedFeature;

    btn.classList.toggle("active-edit-vertex", isActive);
  });
}

function stopActiveVertexEdit(map = getMap(), restoreGeometry = false, options = {}) {
  const hadActiveEdit = !!modifyInteraction;

  if (restoreGeometry && selectedFeature && originalGeometry) {
    selectedFeature.setGeometry(originalGeometry);
  }

  if (modifyInteraction && map) {
    map.removeInteraction(modifyInteraction);
  }

  modifyInteraction = null;
  originalGeometry = null;
  removePolygonVertices(map);

  if (editEscHandler) {
    document.removeEventListener("keydown", editEscHandler);
    editEscHandler = null;
  }

  updateEditButtonState();

  if (hadActiveEdit && !options.silentStatus) {
    MapUtils.setCommand('Ready');
    MapUtils.setStatus(restoreGeometry ? 'Edit vertex cancelled.' : 'Edit vertex stopped.');
    MapUtils.setMessage('—');
  }
}

document.addEventListener("draw-summary-edit-cancel", (event) => {
  stopActiveVertexEdit(getMap(), false, {
    silentStatus: !!event.detail?.silentStatus
  });
});

function isSameCoordinate(a, b) {
  return Array.isArray(a) &&
    Array.isArray(b) &&
    a.length >= 2 &&
    b.length >= 2 &&
    a[0] === b[0] &&
    a[1] === b[1];
}

function showPolygonVertices(feature, map) {

  removePolygonVertices(map);

  if (!feature || !map) return;

  const geometry = feature.getGeometry();

  if (geometry.getType() !== 'Polygon') return;

  vertexSource = new VectorSource();

  const coordinates = geometry.getCoordinates()[0] || [];
  const visibleCoordinates = coordinates.length > 1 &&
    isSameCoordinate(coordinates[0], coordinates[coordinates.length - 1])
    ? coordinates.slice(0, -1)
    : coordinates;

  visibleCoordinates.forEach(coord => {

    const vertexFeature = new Feature({
      geometry: new Point(coord)
    });

    vertexSource.addFeature(vertexFeature);
  });

  vertexLayer = new VectorLayer({
    source: vertexSource,
    zIndex: 10000,

    style: new Style({
      image: new CircleStyle({
        radius: 5,

        fill: new Fill({
          color: '#ffffff'
        }),

        stroke: new Stroke({
          color: '#ff0000',
          width: 2
        })
      })
    })
  });

  map.addLayer(vertexLayer);

  vertexGeometryChangeKey = geometry.on('change', () => {
    showPolygonVertices(feature, map);
  });
}

function removePolygonVertices(map) {

  if (vertexGeometryChangeKey) {
    unByKey(vertexGeometryChangeKey);
    vertexGeometryChangeKey = null;
  }

  if (vertexLayer) {
    map.removeLayer(vertexLayer);
    vertexLayer = null;
  }

  if (vertexSource) {
    vertexSource.clear();
    vertexSource = null;
  }
}

async function reRunSpatialQuery(feature) {

  const map = getMap();
  if (!map || !feature) 
  {
    console.error("Feature is NULL");
    return;
  }

  const originalGeom = feature.getGeometry();

  let payload;

  if (originalGeom.getType() === 'Circle') {
    const polygon = fromCircle(originalGeom, 128);
    const geom4326 = polygon.clone().transform('EPSG:3857', 'EPSG:4326');

    payload = {
      type: "Polygon",
      geometry: new GeoJSON().writeGeometryObject(geom4326)
    };
  } else {
    const geom4326 = originalGeom.clone().transform('EPSG:3857', 'EPSG:4326');

    payload = {
      type: "Polygon",
      geometry: new GeoJSON().writeGeometryObject(geom4326)
    };
  }

  const countryCode = getSelectedCountryCode();
  const languageCode = getSelectedLanguageCode();

  const response = await secureFetch(`${config.API_URL}spatial-search/polygon`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, countryCode, languageCode })
  });

  const data = await response.json();

  const count = getSpatialResultCount(data);

  // format properly
  const addressData = getSpatialResultFeatures(data).map(f => propertiesToAddressRow(f.properties));

  feature.set("addressData", addressData);

  const item = drawHistory.find(d =>
    d.feature === feature ||
    d.feature.getGeometry() === feature.getGeometry()
  );

  if (item) {

    removeFromSelectionCount(item.count);
    item.count = count;
    addToSelectionCount(count);
    sendAddressSelectionMessage('modify', item);

  }

  renderPanel();

}

function getNextSequence() {
  if (!drawHistory.length) return 1;

  const maxSeq = Math.max(...drawHistory.map(d => d.seq || 0));
  return maxSeq + 1;
}

function getSummaryFillColor(hexColor) {
  const normalized = String(hexColor || '').trim();
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(normalized);
  if (!match) return 'transparent';

  const [, red, green, blue] = match;
  return `rgba(${parseInt(red, 16)},${parseInt(green, 16)},${parseInt(blue, 16)},0.2)`;
}

/**
 * Add a new entry (called after each draw)
 */
export function addDrawEntry(color, count, feature) {

  const geometryType = feature?.getGeometry()?.getType() || "Polygon";
  const seq = getNextSequence();

  drawHistory.push({
    seq,
    color,
    count,
    feature,
    geometryType
  });

  applyLabelStyle(drawHistory[drawHistory.length - 1]);
  addressSummaryPage = Math.max(1, Math.ceil(drawHistory.length / ADDRESS_SUMMARY_PAGE_SIZE));

  renderPanel();
  queueSaveToBackend();
  sendAddressSelectionMessage('add', drawHistory[drawHistory.length - 1]);
}

/**
 * Render / update panel
 */
function getAddressSummaryPageItems(currentPage, totalPages) {
  if (totalPages <= 9) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage]);
  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page > 1 && page < totalPages) pages.add(page);
  }
  if (currentPage <= 4) {
    [2, 3, 4, 5].forEach(page => pages.add(page));
  }
  if (currentPage >= totalPages - 3) {
    [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1]
      .forEach(page => pages.add(page));
  }

  const sortedPages = [...pages].filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b);
  return sortedPages.reduce((items, page, index) => {
    const previousPage = sortedPages[index - 1];
    if (previousPage && page - previousPage > 1) {
      items.push('ellipsis');
    }
    items.push(page);
    return items;
  }, []);
}

function renderAddressSummaryPageButtons(currentPage, totalPages) {
  return getAddressSummaryPageItems(currentPage, totalPages).map(item => {
    if (item === 'ellipsis') {
      return '<span class="pc-summary-page-ellipsis">...</span>';
    }

    return `
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary pc-summary-page-btn ${item === currentPage ? 'is-active' : ''}"
        data-page="${item}"
        aria-label="Page ${item}"
        ${item === currentPage ? 'aria-current="page"' : ''}
      >${item}</button>
    `;
  }).join('');
}

function getAddressSummaryPage(rows, requestedPage) {
  const totalPages = Math.max(1, Math.ceil(rows.length / ADDRESS_SUMMARY_PAGE_SIZE));
  const page = Math.min(Math.max(Number(requestedPage) || 1, 1), totalPages);
  const start = (page - 1) * ADDRESS_SUMMARY_PAGE_SIZE;
  return { page, totalPages, rows: rows.slice(start, start + ADDRESS_SUMMARY_PAGE_SIZE), start };
}

function renderPanel() {
  

  const createdPanel = !panel;

  if (!panel) {
    panel = document.createElement("div");
    panel.id = "draw-summary-panel";
    panel.className = "tool-panel";
    applyPanelPosition();
    document.body.appendChild(panel);
  }

  if (!summaryDockButton) {
    summaryDockButton = document.createElement('button');
    summaryDockButton.type = 'button';
    summaryDockButton.id = 'draw-summary-panel-dock';
    summaryDockButton.className = 'draw-summary-panel-dock btn btn-light border border-start-0 shadow-sm';
    summaryDockButton.innerHTML = '<i class="bi bi-chevron-left"></i>';
    summaryDockButton.title = 'Restore summary';
    summaryDockButton.setAttribute('aria-label', 'Restore summary');
    summaryDockButton.setAttribute('data-translate-title', 'draw_summary.restore');
    summaryDockButton.setAttribute('data-translate-aria-label', 'draw_summary.restore');
    summaryDockButton.style.display = 'none';
    document.body.appendChild(summaryDockButton);
    registerDock('draw-summary', summaryDockButton, 252, '.draw-panel-toggle');

    summaryDockButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      showSummaryPanel();
    });
  }

  const pageData = getAddressSummaryPage(drawHistory, addressSummaryPage);
  addressSummaryPage = pageData.page;
  const totalAddressCount = drawHistory.reduce((total, item) => total + (Number(item.count) || 0), 0);
  const showDownload = hasRight("data.download");
  let html = `
  <div id="draw-summary-header">
    <b data-translate="draw_summary.header">Address Selection Summary</b>
    <label class="summary-opacity-control" title="Transparency">
      <i class="bi bi-circle-half" aria-hidden="true"></i>
      <span class="summary-opacity-slider">
        <input id="draw-summary-opacity" type="range" min="30" max="100" step="5" value="${Math.round(summaryOpacity * 100)}" aria-label="Address Selection Summary opacity">
        <output for="draw-summary-opacity" style="left:${7 + ((Math.round(summaryOpacity * 100) - 30) / 70) * 40}px">${Math.round(summaryOpacity * 100)}%</output>
      </span>
    </label>
    <button id="draw-summary-minimize-btn" class="btn btn-sm btn-outline-secondary sevenn-panel-toggle" aria-label="Dock to left" title="Dock to left" data-translate-title="address_selection.dock" data-translate-aria-label="address_selection.dock">
      <i class="bi bi-dash-lg"></i>
    </button>
    <button id="draw-panel-close" class="btn-close btn-sm" aria-label="Close" title="Close"></button>
  </div>

  <div class="address-summary-toolbar">
    <div id="draw-summary-total">
      <span data-translate="draw_summary.total_address_count" data-translate-params='${JSON.stringify({ count: totalAddressCount })}'>Total Address Count: ${totalAddressCount}</span>
    </div>
    ${showDownload ? `
      <button type="button" id="download-csv-btn" class="btn btn-sm btn-outline-secondary" title="Download Excel" aria-label="Download Excel" data-translate-title="draw_summary.download_excel" data-translate-aria-label="draw_summary.download_excel">
        <i class="bi bi-download fs-6" aria-hidden="true"></i>
      </button>
    ` : ``}
  </div>
  <div id="table-container">
    <table class="table table-sm table-bordered">
      <thead>
        <tr>
          <th style="width:30px;">#</th>
          <th class="draw-summary-color-col" data-translate="draw_summary.color">Color</th>
          <th class="draw-summary-count-col" data-translate="draw_summary.count">Count</th>
          <th class="draw-summary-options-col" data-translate="draw_summary.options">Options</th>
        </tr>
      </thead>
      <tbody>
  `

    pageData.rows.forEach((item, pageIndex) => {
    const index = pageData.start + pageIndex;
    html += `
        <tr data-index="${index}" class="draw-row">
        <td>${item.seq}</td>

      <td class="draw-summary-color-col draw-summary-color-cell">
        <label class="draw-summary-color-symbol${item.geometryType === "Circle" ? " is-circle" : ""}"
          style="border-color:${item.color};background-color:${getSummaryFillColor(item.color)};"
          title="Change color">
          <input type="color"
            value="${item.color}"
            data-index="${index}"
            class="color-picker" />
        </label>
      </td>
        <td class="draw-summary-count-col">${item.count}</td>
        <td class="draw-summary-options-col">
          <div class="draw-summary-actions">

            <button class="edit-row-btn" data-index="${index}"
              title="Edit"
              data-translate-title="draw_summary.edit_button">
              <i class="bi bi-pencil"></i>
            </button>

            <button class="delete-row-btn" data-index="${index}"
              title="Delete"
              data-translate-title="draw_summary.delete_button">
              <i class="bi bi-trash"></i>
            </button>

          </div>
        </td>
        </tr>
    `;
    });

    html += `</tbody></table></div>
      <div class="pc-summary-pagination" aria-label="Address Selection Summary pagination">
        <button type="button" id="address-summary-prev-page" class="btn btn-sm btn-outline-secondary" ${addressSummaryPage <= 1 ? 'disabled' : ''} aria-label="Previous page" title="Previous page"><i class="bi bi-chevron-left"></i></button>
        <div class="pc-summary-page-list" aria-label="Address Selection Summary pages">${renderAddressSummaryPageButtons(addressSummaryPage, pageData.totalPages)}</div>
        <button type="button" id="address-summary-next-page" class="btn btn-sm btn-outline-secondary" ${addressSummaryPage >= pageData.totalPages ? 'disabled' : ''} aria-label="Next page" title="Next page"><i class="bi bi-chevron-right"></i></button>
        <span class="pc-summary-row-count">${drawHistory.length} rows</span>
      </div>`;

  const rowHeight = 34;
  const maxRows = 5;

  
  panel.innerHTML = html;
  panel.querySelector('#address-summary-prev-page')?.addEventListener('click', () => {
    addressSummaryPage--;
    renderPanel();
  });
  panel.querySelector('#address-summary-next-page')?.addEventListener('click', () => {
    addressSummaryPage++;
    renderPanel();
  });
  panel.querySelectorAll('.pc-summary-page-btn').forEach(button => button.addEventListener('click', () => {
    addressSummaryPage = Number(button.dataset.page);
    renderPanel();
  }));
  applyLanguageTranslations(panel);
  applyLanguageTranslations(summaryDockButton);

  if (summaryMinimized) {
    panel.classList.add('is-minimized');
    panel.style.display = 'none';
    setDockVisible('draw-summary', true);
  } else {
    panel.style.display = 'block';
    setDockVisible('draw-summary', false);
    if (createdPanel) {
      requestAnimationFrame(() => placeFloatingDialog(panel));
    }
  }

  const tableContainer = panel.querySelector("#table-container");

  if (tableContainer) {
    tableContainer.style.scrollBehavior = "smooth";

    if (pageData.rows.length > maxRows) {
      tableContainer.style.maxHeight = `${rowHeight * maxRows}px`;
      tableContainer.style.overflowY = "auto";
    } else {
      const visibleRows = Math.max(pageData.rows.length, 1);
      tableContainer.style.maxHeight = `${Math.min(rowHeight * (visibleRows + 1), rowHeight * maxRows)}px`;
      tableContainer.style.overflowY = "auto";
    }
  }

  panel.querySelectorAll("td, th").forEach(el => {
  el.style.padding = "4px";
  });

  refreshLabelStyles();

  updateEditButtonState();

  panel.querySelectorAll(".edit-row-btn").forEach(btn => {

  btn.onclick = (e) => {
    e.stopPropagation();

    const index = btn.getAttribute("data-index");
    const item = drawHistory[index];

    if (!item || !item.feature) return;

    const map = getMap();
    if (!map) return;

    if (modifyInteraction && selectedFeature === item.feature) {
      stopActiveVertexEdit(map);
      return;
    }

    stopActiveVertexEdit(map);

    selectedFeature = item.feature;

    showPolygonVertices(selectedFeature, map);

    // Store original geometry
    originalGeometry = item.feature.getGeometry().clone();

    modifyInteraction = new Modify({
      features: new Collection([selectedFeature])
    });

    modifyInteraction.on('modifystart', (e) => {
      e.features.forEach(f => {
        if (f !== selectedFeature) {
          e.features.remove(f);
        }
      });
    });

    map.addInteraction(modifyInteraction);
    setEditVertexStatus();
    updateEditButtonState();

    // modifyend
    modifyInteraction.on('modifyend', async (e) => {

      const feature = e.features.getArray().find(f => f === selectedFeature);
      if (!feature) return;

      await reRunSpatialQuery(feature);
      queueSaveToBackend();
      showPolygonVertices(feature, map);
      setEditVertexStatus();
    });

    // ESC cancel
    editEscHandler = (evt) => {
      if (evt.key === "Escape" && modifyInteraction && originalGeometry) {
        stopActiveVertexEdit(map);
      }
    };

    document.addEventListener("keydown", editEscHandler);
  };

});

 const downloadBtn = panel.querySelector("#download-csv-btn");

if (downloadBtn) {

  downloadBtn.onclick = async () => {
    if (!drawHistory.some(item => item?.feature)) {
      alert(translate("draw_summary.no_valid_data"));
      return;
    }

    downloadBtn.disabled = true;
    showLoadingBar();

    try {
      await pendingSavePromise.catch(() => {});
      if (saveDirty) {
        await queueSaveToBackend();
      }

      const response = await secureFetch(`${config.API_URL}spatial-search/draw/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.message || `Export failed with status ${response.status}`);
      }

      if (!payload?.url) {
        throw new Error("Export response did not include a download URL");
      }

      window.location.assign(payload.url);
    } catch (error) {
      console.error("Address selection export failed:", error);
      alert(error.message || translate("export.no_exportable_data"));
    } finally {
      hideLoadingBar();
      downloadBtn.disabled = false;
    }

  };
  }

  // Close button (safe)
const closeBtn = panel.querySelector("#draw-panel-close");
if (closeBtn) {
  closeBtn.onclick = () => {
    stopActiveVertexEdit();
    closeSummaryPanel();
  };
}

const minimizeBtn = panel.querySelector("#draw-summary-minimize-btn");
if (minimizeBtn) {
  minimizeBtn.onclick = () => {
    stopActiveVertexEdit();
    minimizeSummaryPanel();
  };
}

panel.style.opacity = String(summaryOpacity);
let summaryOpacityLabelTimer = null;
panel.querySelector('#draw-summary-opacity')?.addEventListener('input', event => {
  summaryOpacity = Number(event.currentTarget.value) / 100;
  panel.style.opacity = String(summaryOpacity);
  const output = panel.querySelector('output[for="draw-summary-opacity"]');
  if (output) {
    output.textContent = `${event.currentTarget.value}%`;
    output.style.left = `${7 + ((Number(event.currentTarget.value) - 30) / 70) * 40}px`;
    output.classList.add('is-visible');
    window.clearTimeout(summaryOpacityLabelTimer);
    summaryOpacityLabelTimer = window.setTimeout(() => output.classList.remove('is-visible'), 2000);
  }
});

// Draggable (SAFE)
const header = panel.querySelector('#draw-summary-header');
if (header && panel) {
  MapUtils.makeDraggable(panel, header);
}

  // Close button
  document.getElementById("draw-panel-close").onclick = () => {
    stopActiveVertexEdit();
    closeSummaryPanel();
  };

  // Row click highlight
    panel.querySelectorAll(".draw-row").forEach(row => {
    row.onclick = () => {
        const index = row.getAttribute("data-index");
        const item = drawHistory[index];

        if (!item.feature) return;

        if (selectedFeature && selectedFeature !== item.feature) {
            const prevItem = drawHistory.find(d => d.feature === selectedFeature);
            if (prevItem) {
                selectedFeature.setStyle(createStyleFromColor(prevItem.color, prevItem.seq));
            }
        }

        blinkFeature(item.feature, item.color);

        // highlight row UI
        panel.querySelectorAll(".draw-row").forEach(r => r.classList.remove("is-selected"));
        row.classList.add("is-selected");
    };
    });

    panel.querySelectorAll(".color-picker").forEach(input => {
    input.oninput = (e) => {
        const index = e.target.getAttribute("data-index");
        const item = drawHistory[index];
        const newColor = e.target.value;

        item.color = newColor;

        const symbol = e.target.closest('.draw-summary-color-symbol');
        if (symbol) {
          symbol.style.borderColor = newColor;
          symbol.style.backgroundColor = getSummaryFillColor(newColor);
        }

        if (item.feature) {
        item.feature.setStyle(createStyleFromColor(newColor, item.seq));
        }

        queueSaveToBackend();
        sendAddressSelectionMessage('modify', item);
    };
    });

  panel.querySelectorAll(".delete-row-btn").forEach(btn => {

      btn.onclick = (e) => {
        e.stopPropagation();

        const index = btn.getAttribute("data-index");
        const item = drawHistory[index];

        if (!item || !item.feature) return;
        if (item.feature === selectedFeature) {
          stopActiveVertexEdit();
          selectedFeature = null;
        }

        const source = getDrawSource();
        const featureId = item.feature.getId();

        let featureToRemove = null;

        if (featureId) {
          featureToRemove = source.getFeatureById(featureId);
        }

        // fallback (rare case)
        if (!featureToRemove) {
          featureToRemove = source.getFeatures().find(f => f === item.feature);
        }

        if (featureToRemove) {
          source.removeFeature(featureToRemove);
        } else {
          console.warn("Feature not found in drawSource");
        }

        removeFromSelectionCount(item.count);
        drawHistory.splice(index, 1);

        renderPanel();
        queueSaveToBackend();
        sendAddressSelectionMessage('remove', item);
      };

    });

  }

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r},${g},${b},${alpha})`;
}



/**
 * Clear panel + reset state
 */
export function resetDrawSummary() {
  stopActiveVertexEdit();
  drawHistory = [];
  addressSummaryPage = 1;
  resetColorCycle();

  closeSummaryPanel();
}

document.addEventListener('application-reset-active-tools', event => {
  if (event.detail?.preserveAddressAidsResults) {
    stopActiveVertexEdit(getMap(), false, { silentStatus: true });
    return;
  }

  resetDrawSummary();
});

function blinkFeature(feature, color, times = 3, interval = 300) {
  if (!feature) return;

  const labelText = feature.get('selectionNumber') || '';
  const originalStyle = feature.getStyle() || createStyleFromColor(color, labelText);

  const blinkStyle = new Style({
    stroke: new Stroke({
      color: '#FFFF00', // bright yellow blink
      width: 5
    }),
    fill: new Fill({
      color: 'rgba(255,255,0,0.4)'
    })
  });

  let count = 0;
  let visible = false;

  const blinkInterval = setInterval(() => {
    feature.setStyle(visible ? originalStyle : blinkStyle);
    visible = !visible;

    count++;
    if (count >= times * 2) {
      clearInterval(blinkInterval);
      feature.setStyle(originalStyle); // restore
    }
  }, interval);
}

function buildPayload() {
  const format = new GeoJSON();
  const map = getMap();

  const view = map.getView();
  const center = view.getCenter();
  const zoom = view.getZoom();

  const normalizeAddressDataForSave = (addressData) => {
    if (!Array.isArray(addressData)) return [];

    return addressData.filter(row => {
      if (!row || typeof row !== "object") return false;

      return Object.values(row).some(value => {
        if (value === null || value === undefined) return false;
        if (typeof value === "string") return value.trim() !== "";
        return true;
      });
    });
  };

  return drawHistory.map(item => {

    const geom = item.feature.getGeometry();
    let geometry;

    if (geom.getType() === "Circle") {
      geometry = {
        type: "Circle",
        center: transform(geom.getCenter(), 'EPSG:3857', 'EPSG:4326'),
        radius: geom.getRadius()
      };
    } else {
      const geom4326 = geom.clone().transform('EPSG:3857', 'EPSG:4326');
      geometry = format.writeGeometryObject(geom4326);
    }

    return {
      seq: item.seq,
      color: item.color,
      count: item.count,
      geometry: JSON.stringify(geometry),
      addressData: JSON.stringify(normalizeAddressDataForSave(item.feature.get("addressData"))),

      centerLat: center[1],
      centerLon: center[0],
      zoomLevel: zoom
    };
  });
}

async function saveToBackend() {

  const payload = buildPayload();

  const countryId = getSelectedCountryCode();
  const languageId = getSelectedLanguageCode();

  const res = await secureFetch(
    `${config.API_URL}spatial-search/draw/save?countryId=${countryId}&languageId=${languageId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    }
  );

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Save failed ${res.status}: ${errorText}`);
  }
}

let isLoadingFromBackend = false;

function fitLoadedResults(map, features) {
  if (!map || !features.length) return;

  const extent = createEmpty();
  features.forEach(feature => {
    const geometry = feature.getGeometry?.();
    if (geometry) {
      extend(extent, geometry.getExtent());
    }
  });

  if (isEmpty(extent)) return;

  map.getView().fit(extent, {
    padding: [70, 70, 70, 70],
    duration: 800,
    maxZoom: 18
  });
}

export async function loadFromBackend(options = {}) {

  if (isLoadingFromBackend) {
    console.warn("Skipping duplicate load");
    return;
  }

  isLoadingFromBackend = true;

  try {
    await pendingSavePromise.catch(() => {});

    const map = getMap();
    if (!map) return;

    clearSelectionSet();
    addressSummaryPage = 1;

    const countryId = getSelectedCountryCode();
    const languageId = getSelectedLanguageCode();

    if (!countryId || !languageId) return;

    const res = await secureFetch(
      `${config.API_URL}spatial-search/draw/load?countryId=${countryId}&languageId=${languageId}`
    );

    const data = await res.json();

    if (!data.length) {
      drawHistory = [];
      getDrawSource()?.clear();
      resetSelectionCount();
      syncColorCycle([]);
      if (options.openPanel === true) summaryMinimized = false;
      renderPanel();
      if (options.openPanel === true) showSummaryPanel();
      sendAddressSelectionSnapshot([]);
      return;
    }

    const format = new GeoJSON();
    const source = getDrawSource();
    const loadedFeatures = [];

    data.forEach(item => {

      let geometryObj = JSON.parse(item.geometry);

      let feature;

      if (geometryObj?.type === "Circle") {
        feature = new Feature({
          geometry: new CircleGeom(
            transform(geometryObj.center, 'EPSG:4326', map.getView().getProjection()),
            Number(geometryObj.radius)
          )
        });
      } else {
        feature = format.readFeature({
          type: "Feature",
          geometry: geometryObj
        }, {
          dataProjection: 'EPSG:4326',
          featureProjection: map.getView().getProjection()
        });
      }

      feature.setStyle(createStyleFromColor(item.color, item.seq));
      feature.set('selectionNumber', item.seq);
      feature.set("addressData", JSON.parse(item.address_data || "[]"));

      source.addFeature(feature);
      loadedFeatures.push(feature);

      drawHistory.push({
        seq: item.seq,
        color: item.color,
        count: item.count,
        feature,
        geometryType: feature.getGeometry()?.getType() || "Polygon"
      });

      applyLabelStyle(drawHistory[drawHistory.length - 1]);

      addToSelectionCount(item.count);
    });

    syncColorCycle(drawHistory.map(item => item.color));
    if (options.openPanel === true) {
      summaryMinimized = false;
    }
    renderPanel();
    if (options.openPanel === true) {
      showSummaryPanel();
    }
    if (options.fitToResults !== false) fitLoadedResults(map, loadedFeatures);
    sendAddressSelectionSnapshot();

  } finally {
    isLoadingFromBackend = false;
  }
}


export function fitCurrentAddressSelectionResults() {
  const map = getMap();
  if (map) fitLoadedResults(map, drawHistory.map(item => item.feature).filter(Boolean));
}
